import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Users, Mail, Phone, Clock, HelpCircle, Zap } from "lucide-react";

const Support = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleAISearch = async () => {
    setIsSearching(true);
    // Simulate AI search
    setTimeout(() => {
      setAiResponse(`Based on your query "${searchQuery}", here are some relevant help topics: 
      
1. Document Upload Process: To upload documents, navigate to your dashboard and click 'Upload Documents'. We support PDF, DOCX, and image formats.

2. AI Search Features: Use natural language queries like "find contracts signed in 2023" or "locate documents mentioning liability clauses".

3. Export Options: You can export search results in PDF, CSV, or Excel formats with custom metadata.

Would you like more specific information about any of these topics?`);
      setIsSearching(false);
    }, 2000);
  };

  const faqItems = [
    {
      question: "How do I upload documents to ExhibitAI?",
      answer: "You can upload documents by navigating to your dashboard and clicking the 'Upload Documents' button. We support PDF, DOCX, images, and scanned documents. You can upload individual files or bulk upload using our folder selection feature."
    },
    {
      question: "What file formats are supported?",
      answer: "ExhibitAI supports PDF, DOCX, DOC, TXT, JPEG, PNG, TIFF, and most common document formats. We also have OCR capabilities for scanned documents and images containing text."
    },
    {
      question: "How accurate is the AI search?",
      answer: "Our AI search achieves 95%+ accuracy for most legal document types. The system continuously learns and improves based on user feedback and query patterns."
    },
    {
      question: "Can I collaborate with my team?",
      answer: "Yes, Pro and Enterprise plans include team collaboration features. You can share document libraries, search results, and create shared workspaces for your legal team."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use enterprise-grade encryption, SOC 2 compliance, and follow strict data protection protocols. Your documents are encrypted both in transit and at rest."
    },
    {
      question: "How do I export search results?",
      answer: "After performing a search, you can export results using the 'Export' button. Choose from PDF, CSV, or Excel formats and customize the included metadata and document references."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary-glow rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">E</span>
            </div>
            <span className="text-xl font-bold">ExhibitAI</span>
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
          Help & Support
        </h1>
        <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
          Get the help you need with our AI-powered search, community forum, and expert support team.
        </p>
      </section>

      {/* AI-Powered Help Search */}
      <section className="container mx-auto px-6 mb-16">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              AI-Powered Help Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Ask anything about ExhibitAI... (e.g., 'How do I upload documents?')"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleAISearch()}
                  />
                </div>
                <Button onClick={handleAISearch} disabled={isSearching}>
                  {isSearching ? "Searching..." : "Search"}
                </Button>
              </div>
              
              {aiResponse && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Zap className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div className="whitespace-pre-line text-sm">{aiResponse}</div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Support Options */}
      <section className="container mx-auto px-6 pb-20">
        <Tabs defaultValue="community" className="max-w-6xl mx-auto">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="community">Community Forum</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="contact">Contact Us</TabsTrigger>
          </TabsList>

          <TabsContent value="community">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Community Forum
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Recent Discussions</h3>
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">Best practices for document organization</h4>
                            <p className="text-sm text-muted-foreground">How do you organize thousands of case documents?</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary">Tips</Badge>
                              <span className="text-xs text-muted-foreground">12 replies</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">AI search query optimization</h4>
                            <p className="text-sm text-muted-foreground">Tips for writing more effective search queries</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary">AI Search</Badge>
                              <span className="text-xs text-muted-foreground">8 replies</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">Integration with case management systems</h4>
                            <p className="text-sm text-muted-foreground">Has anyone integrated ExhibitAI with Clio?</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary">Integrations</Badge>
                              <span className="text-xs text-muted-foreground">15 replies</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Popular Categories</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 border rounded-lg text-center hover:bg-muted/50 cursor-pointer">
                        <MessageCircle className="w-6 h-6 mx-auto mb-2 text-primary" />
                        <div className="font-medium text-sm">General</div>
                        <div className="text-xs text-muted-foreground">245 topics</div>
                      </div>
                      <div className="p-3 border rounded-lg text-center hover:bg-muted/50 cursor-pointer">
                        <Zap className="w-6 h-6 mx-auto mb-2 text-primary" />
                        <div className="font-medium text-sm">AI Features</div>
                        <div className="text-xs text-muted-foreground">189 topics</div>
                      </div>
                      <div className="p-3 border rounded-lg text-center hover:bg-muted/50 cursor-pointer">
                        <HelpCircle className="w-6 h-6 mx-auto mb-2 text-primary" />
                        <div className="font-medium text-sm">How-to</div>
                        <div className="text-xs text-muted-foreground">156 topics</div>
                      </div>
                      <div className="p-3 border rounded-lg text-center hover:bg-muted/50 cursor-pointer">
                        <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
                        <div className="font-medium text-sm">Tips & Tricks</div>
                        <div className="text-xs text-muted-foreground">98 topics</div>
                      </div>
                    </div>
                    
                    <Button className="w-full">
                      Join the Community Forum
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((item, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger>{item.question}</AccordionTrigger>
                      <AccordionContent>{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                <div className="mt-8 p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium mb-2">Didn't find what you're looking for?</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our comprehensive knowledge base has hundreds of articles covering every aspect of ExhibitAI.
                  </p>
                  <Button variant="outline">Browse Knowledge Base</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Contact Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Contact Support
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 mb-6 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Clock className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-orange-800">Paid Account Required</h4>
                        <p className="text-sm text-orange-700 mt-1">
                          Direct support contact is available for Pro and Enterprise plan subscribers.
                        </p>
                        <Link to="/pricing">
                          <Button size="sm" variant="outline" className="mt-3 border-orange-300 text-orange-700 hover:bg-orange-100">
                            Upgrade Account
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  <form className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" placeholder="Your full name" disabled />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" placeholder="your.email@law-firm.com" disabled />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="issueType">Issue Type *</Label>
                      <Select disabled>
                        <SelectTrigger>
                          <SelectValue placeholder="Select issue type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">Technical Issue</SelectItem>
                          <SelectItem value="billing">Billing Question</SelectItem>
                          <SelectItem value="feature">Feature Request</SelectItem>
                          <SelectItem value="training">Training & Support</SelectItem>
                          <SelectItem value="account">Account Management</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Textarea 
                        id="description" 
                        placeholder="Please describe your issue or question in detail..."
                        rows={4}
                        disabled
                      />
                    </div>
                    
                    <Button className="w-full" disabled>
                      Submit Support Request
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Other Ways to Reach Us</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-medium">Email Support</div>
                        <div className="text-sm text-muted-foreground">support@exhibitai.com</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-medium">Phone Support</div>
                        <div className="text-sm text-muted-foreground">(555) 123-4567</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <div>
                        <div className="font-medium">Business Hours</div>
                        <div className="text-sm text-muted-foreground">Mon-Fri: 9AM-6PM EST</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Response Times</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Trial Users</span>
                        <Badge variant="secondary">Community Forum</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Pro Plan</span>
                        <Badge variant="secondary">24-48 hours</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>Enterprise</span>
                        <Badge variant="secondary">2-4 hours</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Emergency Support</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      For critical issues affecting your legal practice, Enterprise customers can access our emergency support line.
                    </p>
                    <Button variant="outline" className="w-full">
                      Learn About Enterprise
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
};

export default Support;
